import { useEffect, useState } from 'react';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';

const STATUS_COLORS = {
  success: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed:  'bg-red-100 text-red-700',
  manual:  'bg-blue-100 text-blue-700',
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '' });

  async function loadPayments(page = 1) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, ...filters });
      const res = await api.get(`/admin/payments?${params}`);
      setPayments(res.data.data);
      setPagination(res.data.pagination);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadPayments(); }, [filters]);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Payments</h1>
          <span className="text-sm text-gray-500">{pagination.total} total</span>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3">
          <select value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="manual">Manual</option>
          </select>
          <select value={filters.type}
            onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
            <option value="">All Types</option>
            <option value="registration">Registration</option>
            <option value="renewal">Renewal</option>
            <option value="subscription">Subscription</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Transaction ID', 'Worker', 'Mobile', 'Type', 'Amount', 'Status', 'Method', 'Date'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400">Loading...</td></tr>
                ) : payments.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400">No payments found</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500 max-w-xs truncate">{p.razorpay_order_id || p.id}</td>
                    <td className="px-4 py-3 text-gray-800">{p.full_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{p.mobile}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{p.payment_type}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">₹{p.amount}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{p.payment_method || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-500">Page {pagination.page} of {pagination.totalPages}</span>
              <div className="flex gap-2">
                {pagination.hasPrev && <Button size="sm" variant="secondary" onClick={() => loadPayments(pagination.page - 1)}>← Prev</Button>}
                {pagination.hasNext && <Button size="sm" variant="secondary" onClick={() => loadPayments(pagination.page + 1)}>Next →</Button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
